import * as React from "react";
import styled, { css } from "styled-components";
import { Storage } from "aws-amplify";

import {
  IPromotion,
  PromotionTemplate
} from "src/modules/promotions/api/promotions.api";
import { productToGTMItem } from "src/core/products/utils/product-to-gtm-item";
import {
  aspectRatioToInteger,
  getTemplateBannerAspect,
  PromotionAspect,
  setTemplateBannerImageDimensionsCSS
} from "src/modules/promotions/utils";
import { useGTMEvent } from "src/core/shared/hooks/useGTMEvent";

export type PromotionBannerProps = Omit<IPromotion, "id">;

interface PromotionBannerConfig extends PromotionBannerProps {
  imageHeightRem: number;
  aspect: PromotionAspect;
}

export const imageHeightRem = 24; // 24rem / 240px

/**
 * Promotion banner component
 */
const PromotionBanner: React.ReactFCWithChildren<PromotionBannerProps> = props => {
  const gtmEvent = useGTMEvent();

  /**
   * Banner configuration
   *
   * Calculate addition attribute based of the selected template.
   */
  const bannerConfig = React.useMemo((): PromotionBannerConfig => {
    const aspect = getTemplateBannerAspect(props.template);

    return {
      ...props,
      imageHeightRem,
      aspect
    };
  }, [ props ]);

  const [ image, setImage ] = React.useState<string>();

  const getBannerImage = React.useCallback(async () => {
    if (props.image.includes("assets/")) {
      const url = await Storage.get(props.image, { level: "public" });

      setImage(url as string);

      if (props.product?.partNumber) {
        gtmEvent("view_promotion", { ecommerce: { items: [ productToGTMItem(props.product) ] } });
      }
    } else {
      setImage(props.image);
    }
  // disabled to prevent multiple gtm events occurring
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ props.image ]);

  React.useEffect(() => {
    getBannerImage();
  }, [ getBannerImage ]);

  return (
    <StyledPromotionBanner {...bannerConfig}>
      <img src={image} />
    </StyledPromotionBanner>
  );
};

const StyledPromotionBanner = styled.div<PromotionBannerConfig>`
  ${({
    imageHeightRem, aspect, template
  }) => css`
    position: relative;
    width: ${imageHeightRem * aspectRatioToInteger(aspect)}rem;
    height: ${imageHeightRem}rem;
    overflow: hidden;

    img {
      position: absolute;
      ${setTemplateBannerImageDimensionsCSS(template, imageHeightRem)};
    }
  `};
`;

export default PromotionBanner;
